import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select } from '../components/ui/select';
import { Plus, Edit2, Trash2, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UniformManagementPage() {
  const [activeTab, setActiveTab] = useState('setup'); // setup, stock, issue, report
  const [uniforms, setUniforms] = useState([]);
  const [stock, setStock] = useState([]);
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedGrade, setSelectedGrade] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    category: 'General',
    description: '',
    genderOption: 'UNISEX'
  });

  const [issueFormData, setIssueFormData] = useState({
    studentId: '',
    studentName: '',
    uniformItemId: '',
    size: '',
    quantityToIssue: 1,
    termId: '1',
    academicYear: '2024'
  });

  // Fetch uniforms on mount
  useEffect(() => {
    const fetchUniforms = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API}/uniforms/items`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (response.ok) {
          const data = await response.json();
          setUniforms(data.items || []);
          setStock(data.stock || []);
        }
      } catch (error) {
        console.error('Error fetching uniforms:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUniforms();
  }, []);

  const handleAddUniform = async (e) => {
    e.preventDefault();

    try {
      const response = await fetch(`${API}/uniforms/items`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const newUniform = await response.json();
        setUniforms([...uniforms, newUniform.item]);
        setFormData({
          name: '',
          category: 'General',
          description: '',
          genderOption: 'UNISEX'
        });
        setShowAddForm(false);
      }
    } catch (error) {
      console.error('Error adding uniform:', error);
      alert('Failed to add uniform item');
    }
  };

  const handleDeleteUniform = async (id) => {
    if (!window.confirm('Are you sure? This will not delete existing issues.')) return;

    try {
      const response = await fetch(`${API}/uniforms/items/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.ok) {
        setUniforms(uniforms.filter(u => u.id !== id));
      }
    } catch (error) {
      console.error('Error deleting uniform:', error);
    }
  };

  const getLowStockItems = () => stock.filter(s => s.quantityAvailable <= s.reorderLevel);

  const getStockQuantity = (item) => item.quantityAvailable ?? item.quantity_available ?? 0;

  const handleIssueUniform = async (e) => {
    e.preventDefault();

    if (!issueFormData.studentId || !issueFormData.uniformItemId || !issueFormData.size) {
      alert('Please fill in all required fields');
      return;
    }

    // Find the stock item
    const stockItem = stock.find(s => (s.uniform_item_id || s.uniformItemId) === issueFormData.uniformItemId && s.size === issueFormData.size);
    if (!stockItem || getStockQuantity(stockItem) < issueFormData.quantityToIssue) {
      alert('Insufficient stock available');
      return;
    }

    try {
      const response = await fetch(`${API}/uniforms/issues`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          studentId: issueFormData.studentId,
          uniformItemId: issueFormData.uniformItemId,
          size: issueFormData.size,
          quantityIssued: issueFormData.quantityToIssue,
          issueDate: new Date().toISOString().split('T')[0],
          termId: issueFormData.termId,
          academicYear: issueFormData.academicYear,
          collectedBy: issueFormData.studentName
        })
      });

      if (response.ok) {
        // Update stock immediately
        setStock(stock.map(s => 
          s.id === stockItem.id 
            ? {
                ...s,
                quantityAvailable: getStockQuantity(s) - issueFormData.quantityToIssue,
                quantityIssued: (s.quantityIssued ?? s.quantity_issued ?? 0) + issueFormData.quantityToIssue
              }
            : s
        ));
        
        setIssueFormData({
          studentId: '',
          studentName: '',
          uniformItemId: '',
          size: '',
          quantityToIssue: 1,
          termId: '1',
          academicYear: '2024'
        });
        
        alert('Uniform issued successfully! Stock has been updated.');
      }
    } catch (error) {
      console.error('Error issuing uniform:', error);
      alert('Failed to issue uniform');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading uniform data...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Uniform Management System</h1>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('setup')}
          className={`px-6 py-3 font-medium border-b-2 ${
            activeTab === 'setup'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
        >
          Grade Setup
        </button>
        <button
          onClick={() => setActiveTab('stock')}
          className={`px-6 py-3 font-medium border-b-2 ${
            activeTab === 'stock'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
        >
          Stock Management
        </button>
        <button
          onClick={() => setActiveTab('issue')}
          className={`px-6 py-3 font-medium border-b-2 ${
            activeTab === 'issue'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
        >
          Issue Uniforms
        </button>
        <button
          onClick={() => setActiveTab('report')}
          className={`px-6 py-3 font-medium border-b-2 ${
            activeTab === 'report'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
        >
          Collection Report
        </button>
      </div>

      {/* Grade Setup Tab */}
      {activeTab === 'setup' && (
        <div className="space-y-4">
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Grade
              </label>
              <Select
                value={selectedGrade}
                onChange={(e) => setSelectedGrade(e.target.value)}
              >
                <option value="">Choose Grade</option>
                <option value="1">Grade 1</option>
                <option value="2">Grade 2</option>
                <option value="3">Grade 3</option>
                <option value="7">Grade 7</option>
              </Select>
            </div>
            <Button
              onClick={() => setShowAddForm(!showAddForm)}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 mr-2" /> Add Item to Grade
            </Button>
          </div>

          {showAddForm && (
            <Card className="p-6 bg-blue-50 border-blue-200">
              <h3 className="text-lg font-semibold mb-4">Add Uniform Item to Grade {selectedGrade}</h3>
              <form onSubmit={handleAddUniform} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Item Name
                    </label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder="e.g., School Shirt"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category
                    </label>
                    <Select
                      value={formData.category}
                      onChange={(e) => setFormData({...formData, category: e.target.value})}
                    >
                      <option value="General">General</option>
                      <option value="Sports">Sports</option>
                      <option value="PE">Physical Education</option>
                    </Select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Gender
                    </label>
                    <Select
                      value={formData.genderOption}
                      onChange={(e) => setFormData({...formData, genderOption: e.target.value})}
                    >
                      <option value="UNISEX">Unisex</option>
                      <option value="MALE">Boys</option>
                      <option value="FEMALE">Girls</option>
                    </Select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Quantity Required
                    </label>
                    <Input
                      type="number"
                      value={formData.quantityToIssue || 0}
                      onChange={(e) => setFormData({...formData, quantityToIssue: parseInt(e.target.value) || 0})}
                      placeholder="e.g., 2"
                      min="0"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Unit Cost ($)
                    </label>
                    <Input
                      type="number"
                      value={0}
                      onChange={() => {}}
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Supplier
                    </label>
                    <Input
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      placeholder="Supplier name"
                    />
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                    Add Item
                  </Button>
                  <Button
                    type="button"
                    onClick={() => setShowAddForm(false)}
                    className="bg-gray-400 hover:bg-gray-500"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </Card>
          )}

          {/* Current Uniforms */}
          <Card className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-100 border-b-2 border-gray-300">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Item Name</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Category</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Gender</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Quantity</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Unit Cost</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Total Cost</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {uniforms.map((uniform) => (
                  <tr key={uniform.id} className="border-b hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{uniform.name}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{uniform.category}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{uniform.genderOption}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{uniform.quantityRequired || 1}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">${Number(uniform.price || 0).toFixed(2)}</td>
                    <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                      ${(Number(uniform.quantityRequired || 1) * Number(uniform.price || 0)).toFixed(2)}
                    </td>
                    <td className="px-6 py-4 text-sm space-x-2">
                      <Button size="sm" className="bg-blue-600 hover:bg-blue-700">
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleDeleteUniform(uniform.id)}
                        className="bg-red-600 hover:bg-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
      )}

      {/* Stock Management Tab */}
      {activeTab === 'stock' && (
        <div className="space-y-4">
          {getLowStockItems().length > 0 && (
            <Card className="p-4 bg-yellow-50 border-yellow-200 border-l-4 border-l-yellow-500">
              <div className="flex gap-3">
                <AlertTriangle className="w-6 h-6 text-yellow-600 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-yellow-900">Low Stock Alert</p>
                  <p className="text-sm text-yellow-700">{getLowStockItems().length} items below reorder level</p>
                </div>
              </div>
            </Card>
          )}

          <Card className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-100 border-b-2 border-gray-300">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Item</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Size</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Available</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Issued</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Reorder Level</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {stock.map((item) => {
                  const isLowStock = getStockQuantity(item) <= item.reorderLevel;
                  return (
                    <tr key={item.id} className={`border-b hover:bg-gray-50 ${isLowStock ? 'bg-yellow-50' : ''}`}>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">{item.name}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{item.size}</td>
                      <td className="px-6 py-4 text-sm font-semibold text-gray-900">{getStockQuantity(item)}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{item.quantityIssued}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{item.reorderLevel}</td>
                      <td className="px-6 py-4 text-sm">
                        {isLowStock ? (
                          <span className="inline-block px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-semibold">
                            LOW STOCK
                          </span>
                        ) : (
                          <span className="inline-block px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold">
                            OK
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <Button size="sm" className="bg-blue-600 hover:bg-blue-700">
                          <Edit2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>
        </div>
      )}

      {/* Issue Uniforms Tab */}
      {activeTab === 'issue' && (
        <div className="space-y-4">
          <Card className="p-6 bg-blue-50 border-blue-200">
            <h3 className="text-lg font-semibold mb-4">Issue Uniforms to Student</h3>
            <form onSubmit={handleIssueUniform} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Student Name / ID
                  </label>
                  <Input
                    value={issueFormData.studentName}
                    onChange={(e) => setIssueFormData({...issueFormData, studentName: e.target.value, studentId: e.target.value})}
                    placeholder="Enter student name or ID"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Uniform Item
                  </label>
                  <Select
                    value={issueFormData.uniformItemId}
                    onChange={(e) => setIssueFormData({...issueFormData, uniformItemId: e.target.value, size: ''})}
                    required
                  >
                    <option value="">Select Item</option>
                    {uniforms.map(u => (
                      <option key={u.id} value={u.id}>{u.name} ({u.category})</option>
                    ))}
                  </Select>
                </div>

                {issueFormData.uniformItemId && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Size & Available Stock
                      </label>
                      <Select
                        value={issueFormData.size}
                        onChange={(e) => setIssueFormData({...issueFormData, size: e.target.value})}
                        required
                      >
                        <option value="">Select Size</option>
                        {stock
                          .filter(s => (s.uniform_item_id || s.uniformItemId) === issueFormData.uniformItemId && getStockQuantity(s) > 0)
                          .map(s => (
                            <option key={s.id} value={s.size}>
                              {s.size} ({getStockQuantity(s)} available)
                            </option>
                          ))}
                      </Select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Quantity to Issue
                      </label>
                      <Input
                        type="number"
                        value={issueFormData.quantityToIssue}
                        onChange={(e) => setIssueFormData({...issueFormData, quantityToIssue: parseInt(e.target.value) || 1})}
                        min="1"
                        max={issueFormData.size ? (() => {
                          const selectedStockItem = stock.find(s => (s.uniform_item_id || s.uniformItemId) === issueFormData.uniformItemId && s.size === issueFormData.size);
                          return selectedStockItem ? getStockQuantity(selectedStockItem) : 1;
                        })() : 1}
                        required
                      />
                    </div>
                  </>
                )}
              </div>

              <div className="flex gap-2">
                <Button type="submit" className="bg-green-600 hover:bg-green-700">
                  Issue Uniform (Stock will be updated automatically)
                </Button>
                <Button
                  type="button"
                  onClick={() => setIssueFormData({ studentId: '', studentName: '', uniformItemId: '', size: '', quantityToIssue: 1, termId: '1', academicYear: '2024' })}
                  className="bg-gray-400 hover:bg-gray-500"
                >
                  Clear
                </Button>
              </div>
            </form>
          </Card>

          {/* Stock Availability by Size */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Available Stock by Item & Size</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-100 border-b-2 border-gray-300">
                  <tr>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Item</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Size</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Available</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Issued</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {stock.map((item) => {
                    const isLowStock = getStockQuantity(item) <= item.reorderLevel;
                    const outOfStock = getStockQuantity(item) === 0;
                    
                    return (
                      <tr key={item.id} className={`border-b hover:bg-gray-50 ${outOfStock ? 'bg-red-50' : isLowStock ? 'bg-yellow-50' : ''}`}>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">{item.name}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">{item.size}</td>
                        <td className="px-6 py-4 text-sm font-semibold text-gray-900">{getStockQuantity(item)}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">{item.quantityIssued}</td>
                        <td className="px-6 py-4 text-sm">
                          {outOfStock ? (
                            <span className="inline-block px-3 py-1 bg-red-100 text-red-800 rounded-full text-xs font-semibold">
                              OUT OF STOCK
                            </span>
                          ) : isLowStock ? (
                            <span className="inline-block px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-semibold">
                              LOW STOCK
                            </span>
                          ) : (
                            <span className="inline-block px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold">
                              OK
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {/* Collection Report Tab */}
      {activeTab === 'report' && (
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">Uniform Collection Report by Grade</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {['Grade 1', 'Grade 2', 'Grade 3', 'Grade 7'].map((grade) => (
              <Card key={grade} className="p-4 bg-gray-50 border-l-4 border-blue-500">
                <p className="text-sm text-gray-600">{grade}</p>
                <p className="text-2xl font-bold text-gray-900">85%</p>
                <p className="text-xs text-gray-500">Collection status</p>
                <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-green-600 h-2 rounded-full" style={{width: '85%'}}></div>
                </div>
              </Card>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
